import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, Printer, User, Building2, Briefcase } from "lucide-react";
import { format } from "date-fns";

import Card from "../../../shared/components/Card";
import Button from "../../../shared/components/Button";
import Spinner from "../../../shared/components/Spinner";

import patientsApi from "../services/patientsApi";

const PatientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPatient();
  }, [id]);

  const fetchPatient = async () => {
    setLoading(true);

    try {
      const response = await patientsApi.getPatient(id);
      setPatient(response.data);
    } catch (error) {
      console.error("Failed to load patient", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-20 text-gray-500">
        Patient not found
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">

          <button
            onClick={() => navigate("/patients")}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>

          <div>
            <h1 className="text-2xl font-bold">
              {patient.full_name}
            </h1>

            <p className="text-gray-500">
              Patient ID: {patient.patient_code}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="secondary" icon={Printer} onClick={handlePrint}>
            Print
          </Button>

          <Button
            variant="primary"
            icon={Edit}
            onClick={() => navigate(`/patients/${id}/edit`)}
          >
            Edit
          </Button>
        </div>
      </div>

      {/* BASIC INFO */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <User size={18} />
          <h3 className="text-lg font-semibold">Basic Information</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

          <div>
            <p className="text-sm text-gray-500">Full Name</p>
            <p className="font-medium">{patient.full_name}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Gender</p>
            <p>{patient.gender}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Date of Birth</p>
            <p>
              {patient.date_of_birth
                ? format(new Date(patient.date_of_birth), "dd MMM yyyy")
                : "-"}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Blood Group</p>
            <p>{patient.blood_group || "-"}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Nationality</p>
            <p>{patient.nationality}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Marital Status</p>
            <p>{patient.marital_status || "-"}</p>
          </div>

        </div>
      </Card>

      {/* CONTACT */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">Contact Information</h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

          <div>
            <p className="text-sm text-gray-500">Mobile</p>
            <p>{patient.mobile || "-"}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Email</p>
            <p>{patient.email}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Personal Email</p>
            <p>{patient.personal_email || "-"}</p>
          </div>

        </div>
      </Card>

      {/* ORGANIZATION */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Building2 size={18} />
          <h3 className="text-lg font-semibold">Department</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

          <div>
            <p className="text-sm text-gray-500">Department</p>
            <p>{patient.department?.name}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Department Code</p>
            <p>{patient.department?.code}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Organization</p>
            <p>{patient.department?.organization?.name}</p>
          </div>

        </div>
      </Card>

      {/* DESIGNATION */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Briefcase size={18} />
          <h3 className="text-lg font-semibold">Designation</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

          <div>
            <p className="text-sm text-gray-500">Designation</p>
            <p>{patient.designation?.name}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Grade</p>
            <p>{patient.designation?.grade}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Level</p>
            <p>{patient.designation?.level}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Joining Date</p>
            <p>
              {patient.joining_date
                ? format(new Date(patient.joining_date), "dd MMM yyyy")
                : "-"}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Employment Type</p>
            <p>{patient.employment_type}</p>
          </div>

        </div>
      </Card>

      {/* OHC */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">OHC / Clinic</h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

          <div>
            <p className="text-sm text-gray-500">Clinic</p>
            <p>{patient.ohc?.name}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Code</p>
            <p>{patient.ohc?.code}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Type</p>
            <p>{patient.ohc?.type_label}</p>
          </div>

          <div className="col-span-2">
            <p className="text-sm text-gray-500">Address</p>
            <p>{patient.ohc?.address?.full}</p>
          </div>

        </div>
      </Card>

      {/* EMERGENCY */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">Emergency Contact</h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

          <div>
            <p className="text-sm text-gray-500">Name</p>
            <p>{patient.emergency_contact?.name || "-"}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Relation</p>
            <p>{patient.emergency_contact?.relation || "-"}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Phone</p>
            <p>{patient.emergency_contact?.phone || "-"}</p>
          </div>

        </div>
      </Card>

      {/* MEDICAL */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">Medical Information</h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

          <div>
            <p className="text-sm text-gray-500">Allergies</p>
            <p>{patient.medical_info?.allergies || "-"}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Existing Conditions</p>
            <p>{patient.medical_info?.existing_conditions || "-"}</p>
          </div>

        </div>
      </Card>

    </div>
  );
};

export default PatientDetailPage;